import React from 'react';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import TestBulletinCard, { TestBulletinItem } from '@/features/bulletins-test/TestBulletinCard';

interface BulletinMasonryProps {
  items: TestBulletinItem[];
  likedMap: Record<string, boolean>;
  onLike: (id: string) => Promise<void>;
  onOpen: (item: TestBulletinItem) => void;
  onDelete?: (id: string) => Promise<void>;
  uploadProgress?: { progress: number; state: string };
}

const BulletinMasonry: React.FC<BulletinMasonryProps> = ({ items, likedMap, onLike, onOpen, onDelete, uploadProgress }) => {
  const is2xl = useBreakpoint('2xl');
  const isXl = useBreakpoint('xl');
  const isLg = useBreakpoint('lg');
  const isSm = useBreakpoint('sm');

  const numColumns = React.useMemo(() => {
    if (is2xl) return 7;
    if (isXl) return 6;
    if (isLg) return 5;
    if (isSm) return 3;
    return 2; // Use 2 columns for mobile as a baseline
  }, [is2xl, isXl, isLg, isSm]);

  const columns = React.useMemo(() => {
    const itemsWithSizes = items.map(item => ({
      ...item,
      isLarge: (parseInt(item.id.substring(0, 8), 16) % 100) < 10,
    }));

    const cols: (typeof itemsWithSizes)[] = Array.from({ length: numColumns }, () => []);
    const colHeights = Array(numColumns).fill(0);

    itemsWithSizes.forEach(item => {
      const shortestColIndex = colHeights.indexOf(Math.min(...colHeights));
      cols[shortestColIndex].push(item);
      // Give large items more "weight" in the height calculation for better distribution
      colHeights[shortestColIndex] += item.isLarge ? 1.5 : 1;
    });

    return cols;
  }, [items, numColumns]);

  return (
    <div className="py-5">
      <div className="w-full max-w-[1800px] mx-auto px-2 md:px-4">
        <div className="flex flex-row gap-1 md:gap-1.5">
        {columns.map((col, i) => (
          <div key={i} className="flex flex-1 flex-col gap-1.5 min-w-0">
            {col.map(item => (
              <TestBulletinCard
                key={item.id}
                item={item}
                liked={!!likedMap[item.id]}
                onLike={onLike}
                onOpen={onOpen}
                onDelete={onDelete}
                uploadProgress={item.id.startsWith('pending-') ? uploadProgress : undefined}
                isLarge={(item as any).isLarge}
              />
            ))}
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default BulletinMasonry;

